import { Channel, chatService, GetMessagesParams, Message } from 'services/chat';
import useSWR, { mutate } from 'swr';
import useSWRInfinite from 'swr/infinite';

const getMessagesKey = (
	channelId: string,
	params: GetMessagesParams
): ['messages', string, GetMessagesParams] => ['messages', channelId, params];

export function useMessages(channelId: string, params: GetMessagesParams = {}) {
	const messageFetcher = async (key: ['messages', string, GetMessagesParams]) => {
		try {
			const result = await chatService.getMessages(key[1], key[2]);
			if (!result || result.length === 0) {
				console.warn(
					`No messages found for channel ${key[1]}. Check if channel exists or API is working correctly.`
				);
			}
			return result || [];
		} catch (error) {
			console.error('Error fetching messages:', error);
			throw error;
		}
	};

	const {
		data,
		error,
		size,
		setSize,
		isLoading,
		isValidating,
		mutate: mutateMessages,
	} = useSWRInfinite<Message[]>(
		(index: number): ['messages', string, GetMessagesParams] | null => {
			if (!channelId || channelId.trim() === '') {
				console.warn('No valid channelId provided to useMessages, skipping fetch');
				return null;
			}

			// For pagination, only include lastMessageId if we're past the first page
			let keyParams: GetMessagesParams = { ...params };
			if (index > 0 && data && data[index - 1] && data[index - 1].length > 0) {
				const lastMessage = data[index - 1][data[index - 1].length - 1];
				if (lastMessage && lastMessage.id) {
					keyParams.lastMessageId = lastMessage.id;
				}
			} else if (index > 0) {
				// If we're trying to get a page beyond the first but there's no data,
				// return null to stop fetching more pages
				return null;
			}

			return getMessagesKey(channelId, keyParams);
		},
		messageFetcher,
		{
			revalidateOnFocus: false,
			revalidateIfStale: false,
			revalidateOnMount: true,
			dedupingInterval: 5000,
		}
	);

	// Safely handle data flattening
	const messages = Array.isArray(data) ? data.filter(Boolean).flat().filter(Boolean).reverse() : [];

	const isReachingEnd =
		!data || !data.length || data[data.length - 1]?.length < (params.limit || 10);

	const loadMore = () => {
		if (!isLoading && !isReachingEnd) {
			setSize(size + 1);
			return true; // Return true to indicate that new data is being loaded
		}
		return false; // Return false if no new data is being loaded
	};

	if (error) {
		console.error('Error in useMessages hook:', error);
	}

	return {
		messages,
		error,
		isLoading,
		isValidating,
		loadMore,
		mutateMessages,
		isReachingEnd,
	};
}

export function useChannel(channelId: string) {
	const {
		data: channel,
		error,
		mutate: mutateChannel,
	} = useSWR<Channel>(channelId ? ['channel', channelId] : null, () =>
		chatService.getChannel(channelId)
	);

	return {
		channel,
		error,
		mutateChannel,
	};
}

export function useSendMessage() {
	const sendMessage = async (channelId: string, payload: any) => {
		try {
			const newMessage = await chatService.sendMessage(channelId, payload);
			// Optimistically update messages
			await mutate(
				(key) => Array.isArray(key) && key[0] === 'messages' && key[1] === channelId,
				async (currentData) => {
					// If no data yet, initialize with the new message
					if (!currentData || !Array.isArray(currentData)) {
						return [[newMessage]];
					}

					// Add new message to the first page
					const updatedData = [...currentData];
					if (updatedData[0]) {
						// Make sure updatedData[0] is iterable before using spread
						if (Array.isArray(updatedData[0])) {
							updatedData[0] = [...updatedData[0], newMessage];
						} else {
							// If it's not an array, initialize it as a new array with the message
							updatedData[0] = [newMessage];
						}
					} else {
						updatedData[0] = [newMessage];
					}

					return updatedData;
				},
				{ revalidate: false } // Don't revalidate immediately to avoid race conditions
			);

			return { success: true, data: newMessage, error: null };
		} catch (error) {
			throw error;
		}
	};

	return { sendMessage };
}

export function useEditMessage() {
	const editMessage = async (channelId: string, messageId: string, payload: any) => {
		try {
			const updatedMessage = await chatService.editMessage(channelId, messageId, payload);

			// Update message in cache
			await mutate(
				(key) => Array.isArray(key) && key[0] === 'messages' && key[1] === channelId,
				(messages: Message[] = []) =>
					messages.map((msg) => (msg.id === messageId ? updatedMessage : msg)),
				false
			);

			return { data: updatedMessage, error: null };
		} catch (error) {
			return { data: null, error };
		}
	};

	return { editMessage };
}

export function useDeleteMessage() {
	const deleteMessage = async (channelId: string, messageId: string) => {
		try {
			await chatService.deleteMessage(channelId, messageId);

			// Update message status to deleted in cache
			await mutate(
				(key) => Array.isArray(key) && key[0] === 'messages' && key[1] === channelId,
				(messages: any[] = []) =>
					messages.map((msg) => {
						if (msg.id === messageId) {
							msg.messageStatus = 'deleted';
							msg.contents = [];
							msg.attachments = [];
						}
						return msg;
					}),
				false
			);

			return { error: null };
		} catch (error) {
			return { error };
		}
	};

	return { deleteMessage };
}

export function useSeenChannel() {
	const seenChannel = async (channelId: string) => {
		try {
			await chatService.seenChannel(channelId);

			// Update channel's last message status
			await mutate(
				(key) => Array.isArray(key) && key[0] === 'channel' && key[1] === channelId,
				(currentChannel?: Channel) =>
					currentChannel
						? {
								...currentChannel,
								notificationCount: 0,
							}
						: undefined,
				false
			);

			return { error: null };
		} catch (error) {
			return { error };
		}
	};

	return { seenChannel };
}
